import apiClient from "@/api/apiClient";
import {
  type GetDeviceErrorsCount,
  type GetDeviceErrorsGanttProps,
  type GetDevicesAvailabilityByDeviceTag,
  type GetPaginatedDeviceErrorsProps,
  type PaginatedDeviceError,
} from "@/api/resources/deviceErrors/types";
import {
  createPaginatedRpcSupabaseQuery,
  createRpcQuery,
  spawnGetApiFunction,
  type ApiResourceConfig,
  type ApiRpcResourceConfig,
} from "@/utils/api";

const table = "device_errors";
const query = "*, device:devices!inner(*, device_tags(*), manufacturer:manufacturers(*), model:device_models(*))";
export const apiConfigDeviceErrors: ApiResourceConfig<typeof table, typeof query> = {
  table,
  query,
  searchables: ["title", "description", "device.name"],
  sortables: ["title", "severity", "started_at", "resolved_at", "created_at", "updated_at"],
};

export const apiConfigGetDeviceErrorsRpc: ApiRpcResourceConfig<"get_device_errors"> = {
  searchables: ["_title", "_description", "_device_name"],
  sortables: ["_title", "_severity", "_started_at", "_resolved_at", "_created_at", "_updated_at"],
};

export const getDeviceError = spawnGetApiFunction({ table, query });

const queryByIds =
  "*, device:devices(*, device_tags(*), manufacturer:manufacturers(*), model:device_models(*)), deployment:deployments(*)";
export const getDeviceErrorsByIds = async (ids?: number[]): Promise<PaginatedDeviceError[]> => {
  if (!ids || ids.length === 0) return [];
  const { data, error } = await apiClient.from(table).select(queryByIds).in("id", ids);
  if (error) throw error;
  return data.map((row) => ({
    _id: row.id,
    _title: row.title,
    _description: row.description,
    _severity: row.severity,
    _code: row.code,
    _component: row.component,
    _started_at: row.started_at,
    _resolved_at: row.resolved_at,
    _created_at: row.created_at,
    _updated_at: row.updated_at,
    _device_name: row.device.name,
    _device: row.device,
    _device_tags: row.device.device_tags,
    _device_model: row.device.model,
    _manufacturer: row.device.manufacturer,
    _deployment: row.deployment,
    _x: row.x,
    _y: row.y,
    _z: row.z,
  })) as unknown as PaginatedDeviceError[];
};

export const getPaginatedDeviceErrors = async ({
  deployment_ids,
  device_ids,
  device_tag_ids,
  device_model_ids,
  manufacturer_ids,
  from,
  to,
  search_title,
  dataTableOptions,
  customFilters,
  customOrFilters,
}: GetPaginatedDeviceErrorsProps) => {
  const { data, count, error } = await createPaginatedRpcSupabaseQuery<"get_device_errors">({
    baseOptions: {
      rpcQuery: createRpcQuery(
        "get_device_errors",
        {
          _deployment_ids: deployment_ids,
          _device_ids: device_ids,
          _device_tag_ids: device_tag_ids,
          _device_model_ids: device_model_ids,
          _manufacturer_ids: manufacturer_ids,
          _from: from,
          _to: to,
          _search_title: search_title,
        },
        { count: "estimated" },
      ),
      searchables: apiConfigGetDeviceErrorsRpc.searchables,
      sortables: apiConfigGetDeviceErrorsRpc.sortables,
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  }).overrideTypes<PaginatedDeviceError[]>();
  if (error) throw error;
  return { data: data, count };
};

export const getDeviceErrorsGantt = async ({
  deployment_ids,
  device_tag_ids,
  device_ids,
  from,
  to,
}: GetDeviceErrorsGanttProps) => {
  const { data, error } = await apiClient.rpc("get_device_errors_gantt", {
    _deployment_ids: deployment_ids,
    _device_tag_ids: device_tag_ids,
    _device_ids: device_ids,
    _from: from,
    _to: to,
  });
  if (error) throw error;
  return data ?? [];
};

export const getDeviceErrorsCount = async ({ from, to, devicesIn, interval }: GetDeviceErrorsCount) => {
  const { data, error } = await apiClient
    .rpc("report_nerrors", {
      _from: from,
      _to: to,
      _devicesin: devicesIn,
      _interval: interval,
    })
    .select("*");

  if (error) throw error;
  return data;
};

export const getDevicesAvailabilityByDeviceTag = async ({
  from,
  to,
  deploymentIds,
  deviceTagIds,
}: GetDevicesAvailabilityByDeviceTag) => {
  const { data, error } = await apiClient
    .rpc("get_devices_availability_by_device_tag", {
      _deployment_ids: deploymentIds,
      _device_tag_ids: deviceTagIds,
      _from: from,
      _to: to,
    })
    .select("*");

  if (error) throw error;
  return data;
};
